import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import {
  FiShield, FiUser, FiCheckCircle, FiXCircle,
  FiEdit, FiSave, FiX, FiLock, FiUnlock
} from 'react-icons/fi';

function AdminRolesPermissions() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Simulation - À remplacer par de vraies données
      setTimeout(() => {
        setRoles([
          { id: 'admin', name: 'Administrateur', description: 'Accès complet au système', userCount: 2 },
          { id: 'demenageur', name: 'Déménageur', description: 'Gestion des missions et devis', userCount: 45 },
          { id: 'client', name: 'Client', description: 'Accès client standard', userCount: 230 },
          { id: 'moderator', name: 'Modérateur', description: 'Modération du contenu', userCount: 3 }
        ]);
        
        setPermissions([
          { id: 'users.view', name: 'Voir utilisateurs', category: 'Utilisateurs' },
          { id: 'users.edit', name: 'Modifier utilisateurs', category: 'Utilisateurs' },
          { id: 'users.delete', name: 'Supprimer utilisateurs', category: 'Utilisateurs' },
          { id: 'payments.view', name: 'Voir paiements', category: 'Paiements' },
          { id: 'payments.edit', name: 'Modifier paiements', category: 'Paiements' },
          { id: 'reports.view', name: 'Voir rapports', category: 'Rapports' },
          { id: 'reports.generate', name: 'Générer rapports', category: 'Rapports' },
          { id: 'content.moderate', name: 'Modérer contenu', category: 'Contenu' },
          { id: 'system.config', name: 'Configuration système', category: 'Système' },
          { id: 'subscriptions.manage', name: 'Gérer abonnements', category: 'Abonnements' }
        ]);

        setRolePermissions({
          admin: ['users.view', 'users.edit', 'users.delete', 'payments.view', 'payments.edit', 'reports.view', 'reports.generate', 'content.moderate', 'system.config', 'subscriptions.manage'],
          demenageur: ['payments.view'],
          client: [],
          moderator: ['content.moderate', 'users.view']
        });
        
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setLoading(false);
    }
  };

  const handleTogglePermission = (roleId, permissionId) => {
    setRolePermissions(prev => {
      const current = prev[roleId] || [];
      const updated = current.includes(permissionId)
        ? current.filter(p => p !== permissionId)
        : [...current, permissionId];
      return { ...prev, [roleId]: updated };
    });
  };

  const handleSavePermissions = async (roleId) => {
    try {
      // API call to save permissions
      alert(`Permissions sauvegardées pour le rôle ${roleId}`);
      setEditingRole(null);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const permissionsByCategory = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) acc[perm.category] = [];
    acc[perm.category].push(perm);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-yellow-400 mb-2">Rôles et Permissions</h1>
        <p className="text-gray-400">Gérer les rôles utilisateurs et leurs permissions</p>
      </div>

      {/* Liste des rôles */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-6">Rôles disponibles</h2>
        
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-4">Chargement...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {roles.map((role) => (
              <div
                key={role.id}
                className="bg-gray-900 border border-gray-700 rounded-lg p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <FiShield className="w-6 h-6 text-yellow-500" />
                      <div>
                        <h3 className="text-xl font-bold text-white">{role.name}</h3>
                        <p className="text-gray-400 text-sm">{role.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4 mt-3 text-sm">
                      <span className="text-gray-400">
                        <FiUser className="w-4 h-4 inline mr-1" />
                        {role.userCount} utilisateurs
                      </span>
                      <span className="text-gray-400">
                        <FiCheckCircle className="w-4 h-4 inline mr-1" />
                        {(rolePermissions[role.id] || []).length} permissions
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setEditingRole(editingRole === role.id ? null : role.id)}
                    className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-colors flex items-center"
                  >
                    <FiEdit className="w-4 h-4 mr-2" />
                    {editingRole === role.id ? 'Annuler' : 'Modifier'}
                  </button>
                </div>

                {editingRole === role.id && (
                  <div className="mt-6 pt-6 border-t border-gray-700">
                    <h4 className="text-lg font-bold text-white mb-4">Permissions</h4>
                    <div className="space-y-6">
                      {Object.entries(permissionsByCategory).map(([category, categoryPermissions]) => (
                        <div key={category}>
                          <h5 className="text-sm font-semibold text-gray-300 mb-3 uppercase">{category}</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {categoryPermissions.map((permission) => {
                              const hasPermission = (rolePermissions[role.id] || []).includes(permission.id);
                              return (
                                <label
                                  key={permission.id}
                                  className="flex items-center space-x-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={hasPermission}
                                    onChange={() => handleTogglePermission(role.id, permission.id)}
                                    className="w-5 h-5 text-yellow-600 bg-gray-900 border-gray-700 rounded focus:ring-yellow-500"
                                  />
                                  <span className="text-white text-sm">{permission.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-end space-x-4 pt-4 border-t border-gray-700">
                        <button
                          onClick={() => setEditingRole(null)}
                          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={() => handleSavePermissions(role.id)}
                          className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-colors flex items-center"
                        >
                          <FiSave className="w-4 h-4 mr-2" />
                          Enregistrer
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminRolesPermissions;


